import React, { useEffect, useRef, useState } from 'react';
import styles from './Style.module.css';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useReleaseBookingHold } from '../../Utils/Hooks/useReleaseBookingHold';
import TimeSlotsLoader from './TimeSlotsLoader';
import { formatDate, useSticky, buildPaxesParam, formatToSlotLabel } from '../../Utils/Helper';
import { getSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { TimeSlotsPicker } from './TimeSlotsPicker';
import { BookingDatesPicker } from './BookingDatesPicker';
import { Header } from '../../Components/Header/Header';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useLocation } from '../../Context/Location/LocationContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useValidateBookingSession } from '../../Utils/Hooks/useValidateBookingSession';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { useCheckSlotAvailable } from '../../APICalling/useApiQueries';
import { SlotUnavailablePopup } from '../../Components/Popup/SlotUnavailablePopup/SlotUnavailablePopup';
import { resolveBookingDateTime } from '../../Utils/resolveBookingDateTime';

export const TimePicker = () => {
  useValidateBookingSession();
  const { bookingUpdate, uuid } = useBookingSession();
  const { releaseHold } = useReleaseBookingHold();
  const queryClient = useQueryClient();
  const [isReleasingHold, setIsReleasingHold] = useState(() =>
    !bookingUpdate && Boolean(uuid || getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID))
  );
  const { appointmentTypeID } = useAppointment();
  const { selectedOutletID, selectedOutletContact, selectedOutletWhatsapp } = useLocation();
  const { selectedDate, setSelectedDate, selectedTime, setSelectedTime, setSlotPublicHoliday } = useSchedule();
  const { members } = useGuests();

  const navigate = useNavigate();
  const checkSlotAvailable = useCheckSlotAvailable();
  const selectionRef = useRef({ selectedDate, selectedTime });
  selectionRef.current = { selectedDate, selectedTime };
  const [slotError, setSlotError] = useState("");
  const [isLoadingBookingDates, setIsLoadingBookingDates] = useState(true);
  const [dateAvailability, setDateAvailability] = useState(null);
  // Resolve known dates immediately so unavailable days don't flash a loader.
  const isDateAvailabilityKnown = dateAvailability?.date === selectedDate ||
    Object.prototype.hasOwnProperty.call(dateAvailability?.byDate || {}, selectedDate);
  const isDateAvailable = (dateAvailability?.date === selectedDate
    ? dateAvailability.available : dateAvailability?.byDate?.[selectedDate]) === true;
  const [dateSelectionSource, setDateSelectionSource] = useState(() => {
    const storedDate = getSessionStorageValue(STORAGE_KEYS.DATE);
    const storedSource = getSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE);
    if (storedSource === 'manual' || storedSource === 'auto') {
      return storedSource;
    }
    return storedDate ? 'restored' : 'initial';
  });

  const { isSticky, sentinelRef } = useSticky();

  useEffect(() => {
    // Direct URL entry must release a previous hold before availability loads.
    // A rescheduled appointment is an existing booking, not a temporary hold.
    if (bookingUpdate || !(uuid || getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID))) {
      setIsReleasingHold(false);
      return;
    }

    let cancelled = false;
    setIsReleasingHold(true);
    const releasePreviousHold = async () => {
      await releaseHold();
      setSelectedTime('');
      sessionStorage.removeItem(STORAGE_KEYS.TIME);
      await Promise.all([
        queryClient.resetQueries({ queryKey: ['bookingDates'] }),
        queryClient.resetQueries({ queryKey: ['bookingSlots'] }),
      ]);
      if (!cancelled) setIsReleasingHold(false);
    };
    releasePreviousHold();
    return () => { cancelled = true; };
  }, [bookingUpdate, uuid, releaseHold, setSelectedTime, queryClient]);

  const resetSelectedTime = () => {
    setSelectedTime('');
    sessionStorage.removeItem(STORAGE_KEYS.TIME);
  };

  const handleManualDateSelect = (date) => {
    setSelectedDate(date);
    // setSessionStorageValue(STORAGE_KEYS.DATE, date);
    // setSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE, 'manual');
    // setDateSelectionSource('manual');
    resetSelectedTime();
  };

  const handleAutoDateSelect = (date) => {
    setSelectedDate(date);
    // setSessionStorageValue(STORAGE_KEYS.DATE, date);
    // setSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE, 'auto');
    // setDateSelectionSource('auto');
    resetSelectedTime();
  };

  const continueWithTime = async (time) => {
    if (!time || !isDateAvailable || isLoadingBookingDates || checkSlotAvailable.isPending) return;
    setSlotError("");
    let isPublicHoliday = false;
    const isCurrentSelection = () => selectionRef.current.selectedDate === selectedDate &&
      selectionRef.current.selectedTime === time;

    try {
      const response = await checkSlotAvailable.mutateAsync({
        panel: "customer",
        outlet: selectedOutletID,
        start_date: selectedDate,
        end_date: selectedDate,
        slot: formatToSlotLabel(time),
        paxes: buildPaxesParam(members),
        ...(uuid && { uuid }),
      });
      if (!isCurrentSelection()) return;

      if (response?.success === false || response?.data?.available === false) {
        setSlotError(response?.message || "This slot is no longer available. Please choose another slot/date.");
        return;
      }
      isPublicHoliday = response?.data?.is_public_holiday ?? false;
    } catch (err) {
      if (!isCurrentSelection()) return;
      const errData = err?.response?.data;
      if (errData?.slot_not_available || errData?.data?.available === false) {
        setSlotError(errData?.message || "This slot is no longer available. Please choose another slot/date.");
      } else {
        setSlotError("Unable to check slot availability. Please try again.");
      }
      return;
    }

    setSelectedTime(time);
    setSlotPublicHoliday(selectedDate, time, isPublicHoliday);
    setSessionStorageValue(STORAGE_KEYS.TIME, time);
    navigate('/review-and-confirm-booking');
  };

  const handleTimeSelect = (time) => {
    if (!time || !isDateAvailable || isLoadingBookingDates || checkSlotAvailable.isPending) return;
    setSlotError("");
    selectionRef.current = { selectedDate, selectedTime: time };
    setSelectedTime(time);
    if (!resolveBookingDateTime(selectedDate, time).isNextDay) {
      return continueWithTime(time);
    }
  };

  const handleContinue = () => continueWithTime(selectedTime);

  // Reset dateSelectionSource when date is cleared (outlet change)
  useEffect(() => {
    if (selectedDate === '') {
      setDateSelectionSource('initial');
    }
  }, [selectedDate]);

  // // REDIRECTION TO HOME WHEN VALUE IS NOT AVAILABLE
  useEffect(() => {
    if (slotError) {
      document.body.classList.add('overlay', 'overflow-hidden', 'overlay-index');
    } else {
      document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
    }
    return () => document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
  }, [slotError]);

  useEffect(() => {
    if (appointmentTypeID == null) {
      navigate('/');
    }
  }, [
    appointmentTypeID,
    navigate,
  ]);


  if (isReleasingHold) return <TimeSlotsLoader />;

  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
      <Header 
        title="Select Available Slots"
      />

      {/* Date Tabs */}
      <div className={`${styles.dateTabsContainer}`}>
        <div className={`${styles.selectedDate} container-md space-x space-y`}>
          {formatDate(selectedDate).monthName} {formatDate(selectedDate).yearNum}
        </div>
        <BookingDatesPicker
          className={`${styles.dateTabs} container-md`}
          selectedOutletID={selectedOutletID}
          members={members}
          selectedDate={selectedDate}
          onDateSelect={handleManualDateSelect}
          onAutoDateSelect={handleAutoDateSelect}
          respectSelectedDate={dateSelectionSource !== "initial"}
          formatDate={formatDate}
          onLoadingChange={setIsLoadingBookingDates}
          onAvailabilityChange={setDateAvailability}
           rescheduling={bookingUpdate}
          uuid={uuid}
        />
      </div>
    </div>

    <TimeSlotsPicker
      selectedOutletID={selectedOutletID}
      members={members}
      selectedDate={selectedDate}
      selectedTime={selectedTime}
      phoneNum={selectedOutletContact}
      whatsappNum={selectedOutletWhatsapp}
      onTimeSelect={handleTimeSelect}
      onContinue={handleContinue}
      isWaitingForDates={isLoadingBookingDates || !isDateAvailabilityKnown}
      isDateAvailable={isDateAvailable}
      datesError={dateAvailability?.errorMessage || ""}
      onRetryDates={() => queryClient.refetchQueries({ queryKey: ['bookingDates'] })}
      rescheduling={bookingUpdate}
      uuid={uuid}
      isSlotChecking={checkSlotAvailable.isPending}
    />

    <SlotUnavailablePopup
      active={!!slotError}
      message={slotError}
      onClose={() => setSlotError("")}
    />

    </>
  );
};
